import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  ArrowUpRight,
  BellRing,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Clock3,
  FileText,
  ReceiptText,
  Sparkles,
  Truck,
  Wallet,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import "./Dashboard.css";

const MotionLink = motion(Link);

/**
 * VELQUE — AI-first executive overview.
 * Reuses the app's existing API client, auth context, Sidebar and routes.
 * No sample metrics are used: unavailable values are displayed as an em dash.
 */
export default function Dashboard() {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState({ list: [], summary: null, insight: null });
  const [vendors, setVendors] = useState({ summary: null, insight: null });
  const [invoices, setInvoices] = useState([]);
  const [finance, setFinance] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    const today = new Date().toISOString().slice(0, 10);

    const safely = (promise, fallback = { data: null }) =>
      promise.catch((error) => {
        console.warn("Executive overview data unavailable:", error?.config?.url || "request", error?.message);
        return fallback;
      });

    try {
      const [appointmentsRes, summaryRes, insightRes, vendorsRes, vendorInsightRes, invoicesRes, financeRes] =
        await Promise.all([
          safely(api.get("/appointments", { params: { date: today } }), { data: { appointments: [] } }),
          safely(api.get("/appointments/summary")),
          safely(api.get("/appointments/insight")),
          safely(api.get("/vendors/summary")),
          safely(api.get("/vendors/insight")),
          safely(api.get("/invoices"), { data: { invoices: [] } }),
          safely(api.get("/finance/snapshot")),
        ]);

      const appointmentList = Array.isArray(appointmentsRes.data?.appointments)
        ? appointmentsRes.data.appointments
        : Array.isArray(appointmentsRes.data) ? appointmentsRes.data : [];
      const invoiceList = Array.isArray(invoicesRes.data?.invoices)
        ? invoicesRes.data.invoices
        : Array.isArray(invoicesRes.data) ? invoicesRes.data : [];

      setAppointments({
        list: appointmentList,
        summary: summaryRes.data || null,
        insight: insightRes.data || null,
      });
      setVendors({
        summary: vendorsRes.data || null,
        insight: vendorInsightRes.data || null,
      });
      setInvoices(invoiceList);
      setFinance(financeRes.data || null);

      // Use real events from existing records; don't invent activity.
      const events = [];
      appointmentList.forEach((item) => {
        if (item.created_at) {
          events.push({
            at: item.created_at,
            title: `Appointment booked${item.customer_name ? ` · ${item.customer_name}` : ""}`,
            subtitle: item.service_name || item.service || "Appointment",
            icon: CalendarDays,
            tone: "mint",
            href: "/appointments",
          });
        }
        if (item.status === "cancelled" && item.updated_at) {
          events.push({
            at: item.updated_at,
            title: `Appointment cancelled${item.customer_name ? ` · ${item.customer_name}` : ""}`,
            subtitle: "Appointment status updated",
            icon: BellRing,
            tone: "peach",
            href: "/appointments",
          });
        }
      });
      invoiceList.forEach((item) => {
        if (item.created_at) {
          events.push({
            at: item.created_at,
            title: `Invoice added${item.vendor_name ? ` · ${item.vendor_name}` : ""}`,
            subtitle: item.invoice_number || "Vendor invoice",
            icon: FileText,
            tone: "sand",
            href: "/invoices",
          });
        }
        if (item.payment_status === "paid" && item.paid_at) {
          events.push({
            at: item.paid_at,
            title: `Payment recorded${item.vendor_name ? ` · ${item.vendor_name}` : ""}`,
            subtitle: item.invoice_number || "Invoice marked paid",
            icon: Wallet,
            tone: "mint",
            href: "/invoices",
          });
        }
      });
      events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
      setActivity(events.slice(0, 4));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const priorities = useMemo(
    () => buildPriorities({ appointments, vendors }),
    [appointments, vendors]
  );
  const firstName = user?.name?.trim()?.split(/\s+/)[0] || "there";
  const greeting = getGreeting();
  const dateLabel = new Date().toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const metrics = useMemo(
    () => buildMetrics({ appointments, vendors, finance, invoices }),
    [appointments, vendors, finance, invoices]
  );
  const health = getHealth(vendors, priorities);
  const aiInsights = useMemo(
    () => buildAIInsights({ appointments, vendors, activity, finance }),
    [appointments, vendors, activity, finance]
  );

  return (
    <div className="nexa-exec-shell min-h-screen bg-nexa-paper">
      <Sidebar />
      <main className="nexa-compact min-h-screen lg:ml-[260px]">
        <div className="nexa-exec-page">
          

          {/* AI executive briefing: one summary and up to three next actions. */}
          <motion.section
            className="nexa-brief-hero"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            aria-labelledby="nexa-brief-title"
          >
            <div className="nexa-orbit-art" aria-hidden="true">
              <div className="nexa-orbit-ring ring-one" />
              <div className="nexa-orbit-ring ring-two" />
              <div className="nexa-orbit-core" />
              <div className="nexa-orbit-glow" />
              <span className="nexa-orbit-spark spark-one" />
              <span className="nexa-orbit-spark spark-two" />
              <span className="nexa-orbit-spark spark-three" />
            </div>

            <div className="nexa-brief-copy">
              <div className="nexa-section-kicker"><Sparkles size={14} /> AI EXECUTIVE BRIEFING</div>
              <h2 id="nexa-brief-title">{greeting}, {firstName}.</h2>
              <p className="nexa-brief-priority-line">
                {loading
                  ? "Nexa is reviewing your latest business activity."
                  : Number(vendors.summary?.overdue || 0) > 0
                    ? "1 priority needs your attention today."
                    : Number(appointments.summary?.by_status?.pending || 0) > 0
                      ? `${Number(appointments.summary.by_status.pending)} appointments need confirmation today.`
                      : priorities.length
                        ? `${priorities.length} actions can keep your operations moving today.`
                        : "Your operations look clear today."}
              </p>
              <p className="nexa-brief-amount-line">
                {loading
                  ? "Preparing your action plan…"
                  : Number(vendors.summary?.overdue || 0) > 0
                    ? `${formatINR(vendors.summary.overdue)} overdue.`
                    : Number(appointments.summary?.by_status?.pending || 0) > 0
                      ? `${Number(appointments.summary.by_status.pending)} appointment${Number(appointments.summary.by_status.pending) === 1 ? "" : "s"} awaiting confirmation.`
                      : Number(vendors.summary?.due_today || 0) > 0
                        ? `${formatINR(vendors.summary.due_today)} in vendor payments due today.`
                        : Number(vendors.summary?.waiting || 0) > 0
                          ? `${Number(vendors.summary.waiting)} vendor invoices in the queue.`
                          : "No urgent items were found in the latest available data."}
              </p>
              <MotionLink
                to={priorities[0]?.href || "/finance"}
                className="nexa-brief-cta"
                data-testid="ex-hero-cta"
              >
                {priorities[0]?.id === "overdue-invoices" ? "Review invoices"
                  : priorities[0]?.id === "pending-appointments" ? "Review appointments"
                    : priorities[0]?.id === "vendor-payments" ? "Review payments"
                      : priorities[0]?.id === "vendor-queue" ? "Review vendors"
                        : "Review financial Health"}
                <ArrowUpRight size={15} strokeWidth={2} />
              </MotionLink>
            </div>

            <div className="nexa-brief-divider" aria-hidden="true" />

            <div className="nexa-brief-actions">
              <div className="nexa-actions-heading">
                {loading
                  ? "NEXA IS ANALYSING YOUR BUSINESS"
                  : priorities.length
                    ? `TOP ${Math.min(priorities.length, 3)} ACTION${Math.min(priorities.length, 3) === 1 ? "" : "S"} FOR TODAY`
                    : "TODAY'S STATUS"}
              </div>
              {loading ? (
                <div className="nexa-action-placeholder">Nexa is reviewing your latest data…</div>
              ) : priorities.length ? (
                priorities.slice(0, 3).map((priority, index) => {
                  const Icon = priority.icon;
                  const status = priority.id === "overdue-invoices"
                    ? "critical"
                    : priority.id === "vendor-payments"
                      ? "attention"
                      : "normal";
                  return (
                    <MotionLink
                      key={priority.id}
                      to={priority.href}
                      className={`nexa-brief-action ${priority.tone}`}
                      initial={{ opacity: 0, x: 8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.08 * index, duration: 0.3 }}
                    >
                      <span className="nexa-action-icon"><Icon size={17} strokeWidth={1.8} /></span>
                      <span className="nexa-action-content">
                        <strong>{priority.title}</strong>
                        <small>{priority.description}</small>
                      </span>
                      <span className={`nexa-priority-status ${status}`}>
                        {status === "critical" ? "Critical" : status === "attention" ? "Attention" : "Normal"}
                      </span>
                      <span className="nexa-action-arrow"><ChevronRight size={16} /></span>
                    </MotionLink>
                  );
                })
              ) : (
                <div className="nexa-clear-state">
                  <span className="nexa-action-icon"><CheckCircle2 size={18} /></span>
                  <span><strong>Nothing urgent</strong><small>You're caught up for now.</small></span>
                </div>
              )}
            </div>
          </motion.section>

          <section className="nexa-metric-grid" aria-label="Business metrics">
            {metrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <motion.div
                  key={metric.label}
                  className={`nexa-metric-card tone-${metric.tone}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  <div className="nexa-metric-top">
                    <span className="nexa-metric-icon"><Icon size={17} strokeWidth={1.8} /></span>
                    <span className="nexa-metric-label">{metric.label}</span>
                    <span className="nexa-metric-mark"><ArrowUpRight size={14} /></span>
                  </div>
                  <div className="nexa-metric-value">{metric.value}</div>
                  <div className="nexa-metric-hint">{metric.hint}</div>
                </motion.div>
              );
            })}
          </section>

          <section className="nexa-bottom-grid">
            <motion.div
              className="nexa-health-panel"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
            >
              <div className="nexa-panel-heading">
                <div><span className="nexa-panel-kicker">BUSINESS PULSE</span><h3>Business health</h3></div>
                <Activity size={17} className="nexa-heading-icon" />
              </div>
              <div className="nexa-health-body">
                {/* Horizontal business health score */}
                <div className={`nexa-health-ring ${health.score == null ? "is-unknown" : ""}`}>
                  <svg
                    className="nexa-health-ring-svg"
                    viewBox="0 0 120 120"
                    aria-hidden="true"
                  >
                    <defs>
                      <linearGradient
                        id="nexa-health-gradient"
                        x1="0"
                        y1="1"
                        x2="1"
                        y2="0"
                      >
                        <stop offset="0%" stopColor="#35C9A0" />
                        <stop offset="100%" stopColor="#075B49" />
                      </linearGradient>
                    </defs>

                    <circle
                      className="nexa-health-track"
                      cx="60"
                      cy="60"
                      r="49"
                    />

                    {health.score != null && (() => {
                      const score = Math.max(
                        0,
                        Math.min(100, Number(health.score) || 0)
                      );

                      return (
                        <circle
                          className="nexa-health-progress"
                          cx="60"
                          cy="60"
                          r="49"
                          pathLength="100"
                          stroke="url(#nexa-health-gradient)"
                          style={{
                            strokeDasharray: `${score} ${100 - score}`,
                            strokeDashoffset: 0,
                          }}
                        />
                      );
                    })()}
                  </svg>

                  <div className="nexa-health-ring-inner">
                    <strong>{health.score == null ? "—" : health.score}</strong>
                    <span>{health.label}</span>
                  </div>
                </div>

                {/* Keep your existing business health explanation */}
                <div className="nexa-health-copy">
                  <strong>{health.title}</strong>
                  <p>{health.description}</p>
                  <MotionLink to="/finance" className="nexa-text-link">
                    Review Financial Health <ArrowRight size={14} />
                  </MotionLink>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="nexa-quick-panel"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.05 }}
            >
              <div className="nexa-panel-heading">
                <div><span className="nexa-panel-kicker">MOVE THINGS FORWARD</span><h3>Quick actions</h3></div>
                <Sparkles size={17} className="nexa-heading-icon" />
              </div>
              <div className="nexa-quick-grid">
                <QuickAction to="/appointments" icon={CalendarDays} tone="mint" title="Appointment reminders" description="Reduce no-shows" />
                <QuickAction to="/vendors" icon={Truck} tone="sand" title="Vendor payments" description="Review invoices due" />
                <QuickAction to="/invoices" icon={FileText} tone="blue" title="Create an invoice" description="Record a new invoice" />
                <QuickAction to="/finance" icon={Wallet} tone="lilac" title="Financial insights" description="Review performance" />
              </div>
            </motion.div>
          </section>

          {/* AI INSIGHTS — one unified panel with connected rows, not separate cards. */}
          <section className="nexa-ai-insights" aria-labelledby="nexa-ai-insights-title">
            <div className="nexa-ai-insights-header">
              <div className="nexa-ai-insights-title-group">
                <span className="nexa-ai-insights-title-icon"><Sparkles size={16} strokeWidth={1.8} /></span>
                <div>
                  <h3 id="nexa-ai-insights-title">Nexa AI insights</h3>
                  <p>Signals from your latest business activity</p>
                </div>
              </div>
              <span className="nexa-ai-insights-count">
                {aiInsights.length ? `${aiInsights.length} ${aiInsights.length === 1 ? "insight" : "insights"}` : "Monitoring"}
              </span>
            </div>
            {aiInsights.length > 0 ? (
              <div className="nexa-ai-insights-list">
                {aiInsights.map((insight) => {
                  const Icon = insight.icon || Sparkles;
                  return (
                    <div className={`nexa-ai-insight-row ${insight.tone || "mint"}`} key={insight.id}>
                      <span className="nexa-ai-insight-icon"><Icon size={15} strokeWidth={1.8} /></span>
                      <p>{insight.text}</p>
                      <span className="nexa-ai-insight-spark" aria-hidden="true"><ArrowUpRight size={14} /></span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="nexa-ai-insights-empty">
                <Sparkles size={15} />
                <span>Nexa will surface recommendations as more business activity is recorded.</span>
              </div>
            )}
          </section>

          <section className="nexa-activity-panel">
            <div className="nexa-panel-heading activity-heading">
              <div><span className="nexa-panel-kicker">LATEST UPDATES</span><h3>Recent activity</h3></div>
              <MotionLink to="/appointments" className="nexa-text-link">View activity <ArrowRight size={14} /></MotionLink>
            </div>
            {activity.length ? (
              <div className="nexa-activity-list">
                {activity.slice(0, 4).map((event, index) => {
                  const Icon = event.icon || Clock3;
                  return (
                    <Link to={event.href || "/"} className="nexa-activity-item" key={`${event.at}-${index}`}>
                      <span className={`nexa-activity-icon ${event.tone || "mint"}`}><Icon size={16} /></span>
                      <span className="nexa-activity-text"><strong>{event.title}</strong><small>{event.subtitle}</small></span>
                      <span className="nexa-activity-time">{relativeTime(event.at)}</span>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="nexa-activity-empty">
                <Clock3 size={17} />
                <span>{loading ? "Loading recent activity…" : "Your recent bookings and payments will appear here."}</span>
              </div>
            )}
          </section>

          <footer className="nexa-exec-footer"><Sparkles size={12} /> A clearer view of your business, powered by Nexa.</footer>
        </div>
      </main>
    </div>
  );
}

function QuickAction({ to, icon: Icon, tone, title, description }) {
  return (
    <MotionLink to={to} className={`nexa-quick-action ${tone}`}>
      <span className="nexa-quick-icon"><Icon size={18} strokeWidth={1.8} /></span>
      <span className="nexa-quick-copy"><strong>{title}</strong><small>{description}</small></span>
      <span className="nexa-quick-arrow"><ArrowUpRight size={15} /></span>
    </MotionLink>
  );
}

function buildPriorities({ appointments, vendors }) {
  const list = [];
  const v = vendors.summary || {};
  const pending = Number(appointments.summary?.by_status?.pending || 0);
  const overdue = Number(v.overdue || 0);
  const dueToday = Number(v.due_today || 0);
  const waiting = Number(v.waiting || 0);

  // Lead with the most consequential action.
  if (overdue > 0) list.push({
    id: "overdue-invoices", tone: "peach", icon: AlertTriangle,
    title: "Review overdue invoices",
    description: `${formatINR(overdue)} needs action`,
    href: "/vendors",
  });

  // Show invoice queue before appointments, matching the executive briefing.
  if (waiting > 0) list.push({
    id: "vendor-queue", tone: "mint", icon: Truck,
    title: `${waiting} vendor invoice${waiting === 1 ? "" : "s"} in the queue`,
    description: "Review and approve pending invoices",
    href: "/vendors",
  });

  if (pending > 0) list.push({
    id: "pending-appointments", tone: "blue", icon: CalendarDays,
    title: "Confirm pending appointments",
    description: `${pending} appointment${pending === 1 ? "" : "s"} awaiting confirmation`,
    href: "/appointments",
  });

  if (dueToday > 0) list.push({
    id: "vendor-payments", tone: "sand", icon: ReceiptText,
    title: "Vendor payments due today",
    description: `${formatINR(dueToday)} due · Review payment queue`,
    href: "/vendors",
  });

  return list.slice(0, 4);
}

function buildAIInsights({ appointments, vendors, activity, finance }) {
  const insights = [];
  const seen = new Set();
  const summary = appointments.summary || {};
  const status = summary.by_status || {};
  const totalAppointments = Number(summary.total ?? appointments.list.length ?? 0);
  const pendingAppointments = Number(status.pending || 0);
  const vipCount = Number(summary.vip_count || 0);
  const cashHealth = vendors.insight?.cash_health;
  const attention = vendors.insight?.attention;

  const add = (id, text, icon = Sparkles, tone = "mint") => {
    if (!text || typeof text !== "string") return;
    const normalized = text.trim().toLowerCase();
    if (!normalized || seen.has(normalized) || insights.length >= 4) return;
    seen.add(normalized);
    insights.push({ id, text: text.trim(), icon, tone });
  };

  if (vipCount > 0) {
    add(
      "vip-schedule",
      `${vipCount} VIP customer${vipCount === 1 ? "" : "s"} in today’s schedule — worth a personal touch.`,
      CalendarDays,
      "mint"
    );
  }

  if (typeof cashHealth === "number" && Number.isFinite(cashHealth)) {
    if (cashHealth < 70) {
      add(
        "cash-flow-pressure",
        "Cash flow may need attention — review overdue invoices before scheduling non-critical payments.",
        Wallet,
        "sand"
      );
    } else if (cashHealth >= 90) {
      add(
        "cash-flow-healthy",
        "Your vendor cash-flow signal is healthy based on the latest available records.",
        Wallet,
        "mint"
      );
    }
  }

  const vendorAttentionLines = Array.isArray(attention)
    ? attention
    : typeof attention === "string" ? [attention] : [];
  vendorAttentionLines.forEach((line, index) => {
    if (insights.length < 4 && typeof line === "string") {
      add(`vendor-insight-${index}`, line, Truck, line.toLowerCase().includes("overdue") ? "sand" : "mint");
    }
  });

  if (pendingAppointments > 0) {
    add(
      "pending-appointments-insight",
      `${pendingAppointments} appointment${pendingAppointments === 1 ? " is" : "s are"} awaiting confirmation; reminders can help reduce no-shows.`,
      CalendarDays,
      "blue"
    );
  } else if (totalAppointments >= 8) {
    add(
      "busy-schedule",
      `A full schedule today with ${totalAppointments} appointments — keep the team aligned on upcoming bookings.`,
      CalendarDays,
      "blue"
    );
  }

  if (finance && Number.isFinite(Number(finance.profit)) && Number(finance.profit) < 0) {
    add(
      "profit-review",
      "Recorded expenses are currently higher than revenue this period; review the finance snapshot for detail.",
      AlertTriangle,
      "sand"
    );
  }

  if (activity.length >= 3) {
    add(
      "recent-activity",
      `${activity.length} recent business updates are available in your activity feed.`,
      Activity,
      "blue"
    );
  }

  return insights.slice(0, 4);
}

function buildMetrics({ appointments, vendors, finance }) {
  const summary = appointments.summary || {};
  const status = summary.by_status || {};
  const vendorSummary = vendors.summary || {};
  const revenue = finance?.revenue;
  const outstanding = vendorSummary.outstanding;

  return [
    {
      label: "Appointments today",
      value: summary.total != null ? Number(summary.total).toLocaleString("en-IN") : String(appointments.list.length || 0),
      hint: `${Number(status.confirmed || 0)} confirmed`, icon: CalendarDays, tone: "mint",
    },
    {
      label: "Revenue this month",
      value: revenue != null ? formatINR(revenue) : "—",
      hint: finance ? "From your finance records" : "Finance data unavailable", icon: Wallet, tone: "blue",
    },
    {
      label: "Expenses this month",
      value: finance?.expenses != null ? formatINR(finance.expenses) : "—",
      hint: finance?.expenses != null ? "From your finance records" : "Expense data unavailable", icon: ReceiptText, tone: "sand",
    },
    {
      label: "Vendor payments due",
      value: outstanding != null ? formatINR(outstanding) : "—",
      hint: vendorSummary.due_today > 0 ? `${formatINR(vendorSummary.due_today)} due today` : "Outstanding vendor balance", icon: Truck, tone: "lilac",
    },
  ];
}

function getHealth(vendors, priorities) {
  const raw = vendors.insight?.cash_health;
  const numeric = typeof raw === "number" && Number.isFinite(raw)
    ? Math.max(0, Math.min(100, Math.round(raw)))
    : null;
  const overdue = Number(vendors.summary?.overdue || 0);

  if (numeric == null) {
    if (overdue > 0) {
      return {
        score: null, label: "Review",
        title: "Start with overdue invoices.",
        description: `${formatINR(overdue)} is overdue. Review the vendor queue to understand the cash-flow impact.`,
      };
    }
    return {
      score: null, label: "Not rated",
      title: "Your business pulse is taking shape.",
      description: "More activity data is needed to calculate a reliable health score.",
    };
  }

  if (numeric >= 85) return {
    score: numeric, label: "Healthy",
    title: "Your business is in good shape.",
    description: overdue > 0
      ? `${formatINR(overdue)} remains overdue. Clearing it can help protect cash flow.`
      : "Your operational signals look healthy based on the latest available data.",
  };
  if (numeric >= 60) return {
    score: numeric, label: "Fair",
    title: "A few areas need attention.",
    description: overdue > 0
      ? `Start by reviewing ${formatINR(overdue)} in overdue invoices.`
      : `${priorities.length} action${priorities.length === 1 ? "" : "s"} can help your operations run more smoothly.`,
  };
  return {
    score: numeric, label: "Needs care",
    title: "Prioritise cash-flow clarity.",
    description: overdue > 0
      ? `${formatINR(overdue)} is overdue. Review vendor obligations and upcoming payments.`
      : "Review pending work and financial activity to identify where attention is needed.",
  };
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function formatINR(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
}

function relativeTime(value) {
  if (!value) return "";
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return "";
  const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60000));
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
